import React, { useContext, useEffect, useState } from "react"
import { Navigate, Route, Routes } from "react-router-dom"

import { getUserCommunities, getUserDetails, updateActivity } from "./services/api/user"
import { useLogger } from "./providers/LoggerProvider"
import { UserContext } from "./providers/UserContext"

import UserService, { UserRole } from "./UserService"
import { DashboardsPage, Logout, MorePage } from "./sites"
import { getTablets, getCompanyPortalProperties } from "./services/api/company"
import { getPath, Tablet } from "src/models/company"
import MyAssignmentsPage from "./sites/myassignments/MyAssignmentsPage"
import AiTextTranslatorPage from "./sites/text/AiTextTranslatorPage"
import MyFilesPage from "./sites/myfiles/MyFilesPage"
import HomePage from "./sites/home/HomePage"
import TranslateFilePage from "./sites/file/TranslateFilePage"
import ProfilePage from "./sites/profile/ProfilePage"
import { Navbar } from "./components/navbar/Navbar"
import KmPage from "./km/pages/KmPage"
import DataSourcesPage from "./sites/data-sources/DataSourcesPage"
import { UserProfile } from "./models/user"
import { Box } from "@mui/material"
import { MainWrapper } from "./components/MainWrapper"
import { useLocalStorage } from "./utils/useLocalStorage"
import { MobileAppBar } from "./components/navbar/MobileAppBar"
import PageWithModal from "./components/page/PageWithModal"
import HelpPage from "./sites/help/HelpPage"
import CompanyPage from "./sites/company/CompanyPage"
import GatedCommunitiesPage from "./sites/company/GatedCommunitiesPage"
import { useLanguages } from "./providers/UserLanguagesContext"
import UpdateLanguageDialog from "./sites/profile/UpdateLanguageDialog"

interface RouteConfig {
  path: string
  element: JSX.Element
  allowed: boolean
}

export const BiPortalApp: React.FC = () => {
  const [open, setOpen] = useLocalStorage("menuToggle", false)
  const [initialized, setInitialized] = useState<boolean>(false)
  const [tabletRoutes, setTabletRoutes] = useState<RouteConfig[]>([])
  const { setUser, setUserCommunities, setCompanyPortalProperties, setTablets, tablets, user, userCommunities } = useContext(UserContext)
  const { logger } = useLogger()
  const { isLanguagesConfigured } = useLanguages()
  const [updateLanguageModalOpen, setUpdateLanguageModalOpen] = useState(!isLanguagesConfigured)
  const getTabletRoutes = (currentUser: UserProfile | null): RouteConfig[] => [
    {
      path: "km-chat",
      element: (
        <PageWithModal
          name="kmChat"
          pageComponent={
            <KmPage
              initialPlaceholder="Ask anything"
              placeholder="Ask anything"
              defaultMessage={() => `Hi ${currentUser?.name}. How can I assist you?`}
            />
          }
        />
      ),
      allowed: UserService.hasRoles([UserRole.KNOWLEDGE_MINER])
    },
    {
      path: "km-generator",
      element: (
        <PageWithModal
          name="kmGenerator"
          pageComponent={
            <KmPage
              generate
              initialPlaceholder="Start creating"
              placeholder="Keep creating..."
              defaultMessage={() => `Hi ${currentUser?.name}. Ready to create?`}
            />
          }
        />
      ),
      allowed: UserService.hasRoles([UserRole.KNOWLEDGE_MINER])
    },
    {
      path: "text",
      element: <PageWithModal name="aiTextTranslator" pageComponent={<AiTextTranslatorPage />} />,
      allowed: true
    },
    { path: "document/:id", element: <TranslateFilePage />, allowed: true },
    {
      path: "bi",
      element: <DashboardsPage />,
      allowed: currentUser?.company.profile === "ORGANIZATION_ADMIN"
    }
  ]

  const getAllowedPaths = (currentUser: UserProfile | null) => {
    return getTabletRoutes(currentUser)
      .filter((r) => r.allowed)
      .map((r) => r.path)
      .map((p) => `${p.split("/")[0]}`)
  }

  const isTabletAllowed = (tablet: Tablet, currentUser: UserProfile | null) => {
    return getAllowedPaths(currentUser).includes(getPath(tablet).split("/")[1])
  }

  const isRouteAllowed = (route: RouteConfig) => {
    const activeTabletPaths = tablets.filter((t) => t.status === "ACTIVE").map((t) => getPath(t).split("/")[1])
    return activeTabletPaths.includes(route.path.split("/")[0]) && route.allowed
  }

  useEffect(() => {
    initUser()
  }, [])

  const initUser = async () => {
    try {
      setInitialized(false)
      await updateActivity()
      const user = await getUserDetails()
      setUser(user)
      setUserCommunities(await getUserCommunities(user.login))
      // should not happen in normal case, added to help debugging
      if (!user.company) logger.error("System - User company not found")
      const portalProps = await getCompanyPortalProperties(user.company.companyId)
      setCompanyPortalProperties(portalProps)
      const tablets = (await getTablets(user.company.companyId)).data
        .filter((t) => ["ACTIVE", "INACTIVE"].includes(t.status))
        .filter((t) => isTabletAllowed(t, user))
        .sort((t1, t2) => t1.priorityGroup - t2.priorityGroup)
      setTablets(tablets)
      setTabletRoutes(getTabletRoutes(user))
      setInitialized(true)
    } catch (e) {
      console.error(e)
      logger.error("System - Failed to init user", e)
    }
  }

  return (
    <MainWrapper>
      {isLanguagesConfigured === false && (
        <UpdateLanguageDialog
          open={updateLanguageModalOpen}
          onClose={() => setUpdateLanguageModalOpen(false)}
          isAlreadySetup={isLanguagesConfigured}
        />
      )}

      {initialized && <MobileAppBar setOpen={setOpen} />}
      {initialized && <Box sx={{ display: "flex", flex: 1, minHeight: 0 }}>
        <Navbar open={open} setOpen={setOpen} />
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
          {initialized && (
            <Routes>
              <Route path="" element={<HomePage />} />
              {UserService.hasRoles([UserRole.BUYER]) && <Route path="files" element={<MyFilesPage />} />}
              {user?.company?.profile === "ORGANIZATION_ADMIN" && (
                <Route path="company" element={<CompanyPage />} />
              )}
              {((userCommunities ?? []).some((c) => c.profile === "GATED_COMMUNITY_ADMIN")) && (
                <Route path="knowledge-bases" element={<GatedCommunitiesPage />} />
              )}
              <Route path="assignments" element={<MyAssignmentsPage />} />
              <Route path="profile" element={<PageWithModal name="myProfile" pageComponent={<ProfilePage />} />} />
              <Route path="help" element={<HelpPage />} />
              <Route path="more" element={<MorePage />} />

              {tabletRoutes
                .filter((r) => isRouteAllowed(r))
                .map((r) => (
                  <Route key={r.path} path={r.path} element={r.element} />
                ))}
              {UserService.hasRoles([UserRole.DATA_SOURCE_ADMIN]) &&
                UserService.hasRoles([UserRole.KNOWLEDGE_MINER]) && (
                  <Route
                    path="data-sources"
                    element={<PageWithModal name="knowledgeBase" pageComponent={<DataSourcesPage />} />}
                  />
                )}

              <Route path="logout" element={<Logout />} />
              <Route path="*" element={<Navigate to="" replace />} />
            </Routes>
          )}
        </Box>
      </Box>}
    </MainWrapper>
  )
}
